import { memo, useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  drawWaveformBarsWindow,
  setupWaveformCanvas,
} from '../../../components/audio-preview/core/waveformRender';
import type { WaveformPeaks } from '../hooks/useWaveformPeaks';

/**
 * タイムラインのオーディオトラック波形描画（Canvas）。
 *
 * コンテンツ全幅の Canvas はズーム時に上限サイズを超えるため、
 * 可視ビューポート幅の Canvas を scrollLeft 位置に重ね、
 * 表示範囲 [scrollLeft, scrollLeft + viewportWidth] のピークのみを毎回描画する。
 * バー描画はプレビュー（WaveformCanvas）と共通ルーチンを使い、見た目を揃える。
 *
 * 2 つの描画モード:
 * - 単一メディアモード（peaks）: トラック全体に 1 メディアの波形を描く（従来）
 * - クリップモード（clips）: MA マルチトラック用。クリップ矩形 + 波形 + ラベルを
 *   タイムライン上の配置位置に描く
 */

/**
 * クリップ波形のカラートーン。
 * - video: 動画内蔵オーディオ（soremo-gray）
 * - plain: `_a`/`_b` オプションなしのオーディオ（soremo-light-gray）
 * - variant: `_a`/`_b` オプションありのオーディオ（soremo-blue）
 */
export type TimelineClipTone = 'video' | 'plain' | 'variant';

/** クリップモードの 1 クリップ分の描画データ */
export interface TimelineClipView {
  key: string;
  /** タイムライン編集モードのクリップ識別子（編集対象クリップのみ） */
  editId?: string;
  /**
   * オプションスロット由来のクリップ（スロットキー）。
   * 候補のテンプレート配置が正のため編集不可（トリム / ナッジ / 削除は保存されない）
   */
  optionSlotKey?: string | null;
  /** タイムライン上の表示開始秒 */
  startSeconds: number;
  /** 表示尺（秒）。null は不明（ピーク実尺から推定） */
  durationSeconds: number | null;
  /** 表示尺の上限（秒）。後着優先カットの可視化用。null = 制限なし */
  maxDurationSeconds?: number | null;
  peaks: WaveformPeaks | null;
  isLoading: boolean;
  error: boolean;
  /** ピークデータ内の読み出し開始秒（動画 offset 表示用） */
  peaksOffsetSeconds?: number;
  /** ピークデータ全体の実尺（秒）。null は不明（20 pairs/秒として推定） */
  peaksDurationSeconds?: number | null;
  /** クリップ左上に描くラベル（ファイル名等） */
  label?: string;
  /** 波形振幅の表示スケール（クリップゲインの可視化用。既定 1） */
  amplitudeScale?: number;
  /** 波形カラートーン（既定 'variant' = 従来のブルー） */
  tone?: TimelineClipTone;
  /**
   * クリップ個別色（#rrggbb）。RPP のリージョン / マーカー色や
   * ルーラーレーン色由来。指定時は tone のパレットより優先される
   */
  colorHex?: string | null;
}

export interface WaveformTrackProps {
  peaks?: WaveformPeaks | null;
  isLoading?: boolean;
  error?: boolean;
  /** クリップモード（指定時は peaks より優先） */
  clips?: TimelineClipView[];
  /** メディア長（秒） */
  duration: number;
  /** 1秒あたりのピクセル数（ズーム倍率） */
  pixelsPerSecond: number;
  /** スクロール位置（px） */
  scrollLeft: number;
  /** 可視ビューポート幅（px） */
  viewportWidth: number;
  /** トラックの高さ（px） */
  height: number;
}

const TrackLane = styled.div<{ $height: number }>`
  position: relative;
  height: ${p => p.$height}px;
  background: var(--soremo-background, #f5f6f7);
  border-top: 1px solid var(--soremo-border);
  border-bottom: 1px solid var(--soremo-border);
  overflow: hidden;
`;

/** クリップラベルの書体は bodytext-11 から算出して Canvas に渡す（drawClips） */
const TrackCanvas = styled.canvas.attrs({ className: 'bodytext-11' })`
  position: absolute;
  top: 0;
  display: block;
  pointer-events: none;
`;

const TrackMessage = styled.div<{ $left: number }>`
  position: absolute;
  top: 50%;
  left: ${p => p.$left}px;
  transform: translateY(-50%);
  padding: 0 12px;
  white-space: nowrap;
  pointer-events: none;
`;

const WAVEFORM_COLOR = 'rgba(0, 154, 206, 0.75)';
const CENTER_LINE_COLOR = 'rgba(0, 154, 206, 0.35)';
const CLIP_LABEL_COLOR = 'rgba(51, 51, 51, 0.85)';

/** トーン別のクリップ配色（wave: 波形 / bg: 矩形背景 / border: 枠線） */
const CLIP_TONE_PALETTES: Record<
  TimelineClipTone,
  { wave: string; bg: string; border: string }
> = {
  // --soremo-gray #53565a
  video: {
    wave: 'rgba(83, 86, 90, 0.75)',
    bg: 'rgba(83, 86, 90, 0.08)',
    border: 'rgba(83, 86, 90, 0.40)',
  },
  // --soremo-light-gray #a7a8a9
  plain: {
    wave: 'rgba(167, 168, 169, 0.90)',
    bg: 'rgba(167, 168, 169, 0.12)',
    border: 'rgba(167, 168, 169, 0.60)',
  },
  // --soremo-blue #009ace
  variant: {
    wave: 'rgba(0, 154, 206, 0.75)',
    bg: 'rgba(0, 154, 206, 0.10)',
    border: 'rgba(0, 154, 206, 0.45)',
  },
};
/** ブラウザデコード / Lambda 生成共通のピーク密度（pairs / 秒）不明時の推定値 */
const FALLBACK_PAIRS_PER_SECOND = 20;

/** #rrggbb → クリップパレットのキャッシュ（描画は毎フレーム呼ばれるため） */
const HEX_PALETTE_CACHE = new Map<string, { wave: string; bg: string; border: string }>();

/**
 * クリップ個別色（#rrggbb）から配色を導出する。
 * alpha は plain トーンと同等の比率（wave 0.85 / bg 0.12 / border 0.55）
 */
function paletteFromHex(colorHex: string): { wave: string; bg: string; border: string } {
  const cached = HEX_PALETTE_CACHE.get(colorHex);
  if (cached) return cached;
  const m = colorHex.match(/^#([0-9a-fA-F]{6})$/);
  if (!m) return CLIP_TONE_PALETTES.plain;
  const value = parseInt(m[1], 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  const palette = {
    wave: `rgba(${r}, ${g}, ${b}, 0.85)`,
    bg: `rgba(${r}, ${g}, ${b}, 0.12)`,
    border: `rgba(${r}, ${g}, ${b}, 0.55)`,
  };
  HEX_PALETTE_CACHE.set(colorHex, palette);
  return palette;
}

/** 表示範囲のピークを Canvas に描画（プレビューと同じバー描画）。
 * マルチチャンネルはチャンネルごとのレーンに分割して描く（DAW ライク） */
function drawWaveform(
  canvas: HTMLCanvasElement,
  peaks: WaveformPeaks,
  duration: number,
  pixelsPerSecond: number,
  scrollLeft: number,
  viewportWidth: number,
  height: number,
): void {
  const width = Math.max(1, Math.round(viewportWidth));
  const ctx = setupWaveformCanvas(canvas, width, height);
  if (!ctx) return;

  const channelCount = Math.max(1, peaks.length);
  const laneHeight = (height - 2) / channelCount;

  // チャンネルごとの中央線
  ctx.fillStyle = CENTER_LINE_COLOR;
  for (let i = 0; i < channelCount; i++) {
    const centerY = 1 + i * laneHeight + laneHeight / 2;
    ctx.fillRect(0, centerY - 0.5, width, 1);
  }

  if (duration <= 0) return;

  const pairCount = Math.floor(peaks[0].length / 2);
  if (pairCount <= 0) return;

  const contentWidth = duration * pixelsPerSecond;
  const xEnd = Math.min(width, Math.ceil(contentWidth - scrollLeft));

  peaks.forEach((channel, i) => {
    drawWaveformBarsWindow(ctx, {
      channels: [channel],
      // ピーク配列の解像度（min/max ペア数 / 秒）を実データから算出
      pairsPerSecond: pairCount / duration,
      originX: -scrollLeft,
      sourceStartSeconds: 0,
      pixelsPerSecond,
      clipLeft: 0,
      clipRight: xEnd,
      y: 1 + i * laneHeight,
      height: laneHeight,
      color: WAVEFORM_COLOR,
    });
  });
}

/**
 * クリップの元ファイル実尺（秒）を解決する（トリム上限の計算用）。
 * メタデータ（peaksDurationSeconds = item.duration）→ ピーク実尺推定の順。
 * どちらも不明なら null
 */
export function resolveClipSourceDuration(clip: TimelineClipView): number | null {
  if (clip.peaksDurationSeconds != null && clip.peaksDurationSeconds > 0) {
    return clip.peaksDurationSeconds;
  }
  if (clip.peaks && clip.peaks.length > 0) {
    return clip.peaks[0].length / 2 / FALLBACK_PAIRS_PER_SECOND;
  }
  return null;
}

/** クリップの表示尺（秒）を解決する（不明時はピーク実尺から推定、上限でカット） */
export function resolveClipDuration(clip: TimelineClipView): number {
  const maxDur = clip.maxDurationSeconds ?? Infinity;
  if (clip.durationSeconds != null && clip.durationSeconds > 0) {
    return Math.min(clip.durationSeconds, maxDur);
  }
  const peaksOffset = clip.peaksOffsetSeconds ?? 0;
  const peaksDuration =
    clip.peaksDurationSeconds ??
    (clip.peaks ? clip.peaks[0].length / 2 / FALLBACK_PAIRS_PER_SECOND : 0);
  return Math.min(Math.max(0, peaksDuration - peaksOffset), maxDur);
}

/** クリップモード: クリップ矩形 + 波形 + ラベルを描画 */
function drawClips(
  canvas: HTMLCanvasElement,
  clips: TimelineClipView[],
  pixelsPerSecond: number,
  scrollLeft: number,
  viewportWidth: number,
  height: number,
): void {
  const width = Math.max(1, Math.round(viewportWidth));
  const ctx = setupWaveformCanvas(canvas, width, height);
  if (!ctx) return;
  const { fontSize, fontFamily } = getComputedStyle(canvas);
  const labelFont = `${fontSize} ${fontFamily}`;

  clips.forEach(clip => {
    const clipDuration = resolveClipDuration(clip);
    if (clipDuration <= 0 && !clip.isLoading) return;
    // 個別色（RPP のリージョン / マーカー / レーン色）があればトーンより優先
    const palette = clip.colorHex
      ? paletteFromHex(clip.colorHex)
      : CLIP_TONE_PALETTES[clip.tone ?? 'variant'];

    // クリップ矩形の可視範囲（Canvas ローカル座標）
    const clipX = clip.startSeconds * pixelsPerSecond - scrollLeft;
    const clipW = Math.max(2, clipDuration * pixelsPerSecond);
    const xs = Math.max(0, clipX);
    const xe = Math.min(width, clipX + clipW);
    if (xe <= xs) return;

    // 背景 + 枠
    ctx.fillStyle = palette.bg;
    ctx.fillRect(xs, 1, xe - xs, height - 2);
    ctx.strokeStyle = palette.border;
    ctx.lineWidth = 1;
    ctx.strokeRect(clipX + 0.5, 1.5, clipW - 1, height - 3);

    // 波形（バー格子はクリップ左端に整列 = クリップ移動に波形が追従する）
    // マルチチャンネルはクリップ内をチャンネルごとのレーンに分割して描く
    if (clip.peaks && clip.peaks.length > 0) {
      const channels = clip.peaks;
      const pairCount = Math.floor(channels[0].length / 2);
      const peaksOffset = clip.peaksOffsetSeconds ?? 0;
      // ピーク密度は「元ファイル実尺」に対して固定。実尺不明時は既知の
      // 生成密度（20 pairs/秒）から推定する。クリップ表示尺（トリム後の長さ）を
      // 基準にすると、終了位置の調整で波形が伸縮して見えてしまうため使わない
      const peaksDuration = clip.peaksDurationSeconds ?? pairCount / FALLBACK_PAIRS_PER_SECOND;
      const pairsPerSecond = peaksDuration > 0 ? pairCount / peaksDuration : FALLBACK_PAIRS_PER_SECOND;
      const laneHeight = (height - 4) / channels.length;

      ctx.save();
      ctx.beginPath();
      ctx.rect(xs, 1, xe - xs, height - 2);
      ctx.clip();
      channels.forEach((channel, i) => {
        drawWaveformBarsWindow(ctx, {
          channels: [channel],
          pairsPerSecond,
          originX: clipX,
          sourceStartSeconds: peaksOffset,
          pixelsPerSecond,
          clipLeft: xs,
          clipRight: xe,
          y: 2 + i * laneHeight,
          height: laneHeight,
          color: palette.wave,
          amplitudeScale: clip.amplitudeScale ?? 1,
        });
      });
      ctx.restore();
    }

    // ラベル（クリップ矩形にクリッピングして描画）
    const labelText = clip.isLoading && !clip.peaks ? `${clip.label ?? ''} 読み込み中...` : clip.label;
    if (labelText && height >= 24) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(xs, 0, xe - xs, height);
      ctx.clip();
      ctx.font = labelFont;
      ctx.textBaseline = 'top';
      ctx.fillStyle = CLIP_LABEL_COLOR;
      ctx.fillText(labelText, Math.max(xs, clipX) + 4, 3);
      ctx.restore();
    }
  });
}

const WaveformTrack = memo(function WaveformTrack({
  peaks = null,
  isLoading = false,
  error = false,
  clips,
  duration,
  pixelsPerSecond,
  scrollLeft,
  viewportWidth,
  height,
}: WaveformTrackProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isClipMode = clips !== undefined;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (isClipMode) {
      drawClips(canvas, clips ?? [], pixelsPerSecond, scrollLeft, viewportWidth, height);
      return;
    }
    if (!peaks || duration <= 0) return;
    drawWaveform(canvas, peaks, duration, pixelsPerSecond, scrollLeft, viewportWidth, height);
  }, [isClipMode, clips, peaks, duration, pixelsPerSecond, scrollLeft, viewportWidth, height]);

  if (isClipMode) {
    return (
      <TrackLane $height={height}>
        <TrackCanvas ref={canvasRef} style={{ left: scrollLeft }} />
      </TrackLane>
    );
  }

  return (
    <TrackLane $height={height}>
      {peaks ? (
        <TrackCanvas ref={canvasRef} style={{ left: scrollLeft }} />
      ) : (
        <TrackMessage $left={scrollLeft} className="bodytext-11 u-text-light-gray">
          {isLoading ? '波形を読み込み中...' : error ? '波形データなし' : ''}
        </TrackMessage>
      )}
    </TrackLane>
  );
});

export default WaveformTrack;
